import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcFlightPlan } from '@assets/icons/32x32';
import { Center, For, Icon, Text, VStack } from '@chakra-ui/react';
import DataSheetCardLink from '@components/common/layout/DataSheetCardLink';
import { useMapContext } from '@components/map/MapContext';
import FlightPlanCard from '@components/map/popup/flightPlan/FlightPlanCard';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectSituationFlightPlansByCodes, selectSituationTime } from '@redux/situation/situation.selectors';
import { sortDate } from '@utils/date.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import { PopupTypeEnum } from '@/types/popup.types';

type Props = {
  flightPlanCodes: string[];
  targetId: string;
  droneCode: string | null;
  position: { x: number; y: number };
};
export default function TargetFlightPlans({ flightPlanCodes, targetId, droneCode, position }: Readonly<Props>) {
  const { mapId } = useMapContext();
  const { formatMessage } = useIntl();
  const flightPlans = useSelectorWithReplayMode(selectSituationFlightPlansByCodes, flightPlanCodes);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const situationTime = useSelectorWithReplayMode(selectSituationTime);

  function handleHistoryPopup() {
    droneCode &&
      updatePopup({
        mapId,
        popup: {
          type: PopupTypeEnum.FLIGHT_PLAN_HISTORY,
          data: {
            targetId,
            droneCode,
            openingDate: situationTime ? new Date(situationTime) : new Date(),
          },
          position,
        },
      });
  }

  return (
    <VStack width="100%" overflow="hidden" gap={0.25} flex={1}>
      {flightPlans.length === 0 ? (
        <Center width="100%" height="360px" backgroundColor="neutral.black">
          <VStack gap={2}>
            <Icon as={EmptyPanelDroneIcon} boxSize={{ base: '80px', '4k': '120px' }} />
            <Text textStyle="screenStd" color="neutral.200" textAlign="center" userSelect="none">
              <FormattedMessage id="targetInfos.tab.drone.emptyDrone" />
            </Text>
          </VStack>
        </Center>
      ) : (
        <VStack
          width="100%"
          gap={0.25}
          maxHeight={{ base: '344px', '4k': '386px' }}
          overflow="auto"
          className="popup-scrollbar"
        >
          <For
            each={flightPlans
              .flatMap((flightPlan) => flightPlan.activations.map((activation) => ({ flightPlan, activation })))
              .sort(({ activation: activation1 }, { activation: activation2 }) =>
                sortDate(activation2.startDate, activation1.startDate),
              )}
          >
            {(info) => (
              <FlightPlanCard
                flightPlan={info.flightPlan}
                activation={info.activation}
                position={position}
                key={info.flightPlan.code}
              />
            )}
          </For>
        </VStack>
      )}
      <DataSheetCardLink
        label={formatMessage({ id: 'targetInfos.tab.flightPlans.history' })}
        icon={IcFlightPlan}
        onClick={handleHistoryPopup}
      />
    </VStack>
  );
}
